import { Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import type { ObjectType } from '../types';
import AdoptCommunityButton from './AdoptCommunityButton';
import {
  useApprovalContext,
  useMyApprovalForObject,
  useSubmitApproval,
} from '../hooks/useApprovals';

interface ApprovalItemActionsProps {
  objectType: ObjectType;
  objectId?: string;
  /** Entity status from the detail response (e.g. 'COMMUNITY'). */
  status?: string;
}

/**
 * Shared approval-workflow entry point for the detail views of the 9 versioned entities:
 * community-version adoption, submitting a working version for approval, and the state
 * of a running request. A live request only exists when the workflow was enabled, so the
 * submit/status part is driven purely by /approvals/for-object/ — the context flag
 * gates only the community-adoption branch.
 */
const ApprovalItemActions = ({ objectType, objectId, status }: ApprovalItemActionsProps) => {
  const { data: context } = useApprovalContext();
  const approvalRequired = !!context?.enabled;
  const isCommunity = status === 'COMMUNITY';
  const { request } = useMyApprovalForObject(objectType, objectId, !isCommunity);
  const submit = useSubmitApproval();

  if (!objectId) return null;

  if (isCommunity) {
    if (!approvalRequired) return null;
    return (
      <div className="px-6 pt-3">
        <AdoptCommunityButton objectType={objectType} objectId={objectId} />
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="px-6 pt-3 space-y-2">
      {request.status === 'rejected' && request.rejection_comment && (
        <div className="rounded border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          Zamítnuto: {request.rejection_comment}
        </div>
      )}
      {(request.status === 'draft' || request.status === 'rejected') && (
        <Button
          size="sm"
          onClick={() => submit.mutate(request.id)}
          disabled={submit.isLoading}
          data-testid="submit-approval-btn"
        >
          <Send />
          Odeslat ke schválení
        </Button>
      )}
      {request.status === 'pending' && (
        <Badge variant="warning" size="lg">
          Čeká na schválení
        </Badge>
      )}
    </div>
  );
};

export default ApprovalItemActions;
